/* smartRecommendationResolver.js, given page context + (optional)
   user profile, return the SINGLE best recommendation to surface.
   Used by `<SmartStrip>` and `<SmartFooterStrip>` so we can drop
   strips site-wide and trust the resolver to pick the right one.

   Resolution priority (highest → lowest):
     1. Workspace pages → stage-aware rec (handled by DashboardPage,
        skipped here)
     2. Logged-out + suburb / estate / H&L page → Conveyancer + Builder bundle
     3. Trade-specific guides (tiling, painting, plumbing, etc.) →
        matching tradie (suppressed for logged-in New Home users)
     4. Finance / cost / FHB / loan guides → Equable
     5. Inspection / defect / handover / contract guides → House Inspect
     6. Generic guides / homepage → stage-aware OR Equable (Thinking
        stage = always Equable; logged-out = Equable + House Inspect
        rotation by URL hash)
     7. Excluded paths (legal/about/policy/etc.) → null

   When logged in (hybrid rule):
     - Page context wins on marketing pages
     - Stage wins on workspace pages (handled elsewhere)
*/

import { getStageRecommendation } from './stageRecommendationMap';

// Paths where we never render a strip, legal/about/policy/contact
// because they're not part of the build journey. Workspace pages
// are intentionally NOT excluded: per user direction the FAB + other
// overlay surfaces should appear on workspace pages too, driven by
// the logged-in user's build stage. The workspace's own inline
// stage-strip components handle their own placements separately.
const EXCLUDED_PATH_PATTERNS = [
  /^\/$/,                     // Homepage - its own sections already carry conversion CTAs
  /^\/privacy/i,
  /^\/terms/i,
  /^\/about(\/|$)/i,
  /^\/contact(\/|$)/i,
  /^\/faq(\/|$)/i,
  /^\/legal/i,
  /^\/policy/i,
  /^\/unsubscribe/i,
  /^\/login/i,
  /^\/signup/i,
  /^\/forgot-password/i,
  /^\/workspace\/login/i,
  /^\/workspace\/signup/i,
  /^\/workspace\/onboarding/i,
  /^\/register-business/i,    // Onboarding wizard - keep page focused on form
  /^\/find\/category\//i,     // Super-cat hub pages have their own conversion sections
];

export function isPathExcluded(pathname) {
  if (!pathname) return false;
  return EXCLUDED_PATH_PATTERNS.some((re) => re.test(pathname));
}

// The One-on-One Support concierge nudge is only offered on new-home-build
// or renovation content (per product direction). Everywhere else the
// catch-all falls back to a builder/cost next-action instead. Keep this an
// allowlist of build/reno URL signals; outdoor, pool, landscaping, garden
// and similar project paths deliberately do NOT qualify.
const BUILD_RENO_PATH_PATTERNS = [
  /building-a-new-home/i,
  /new-home/i,
  /knockdown/i,
  /house-and-land/i,
  /first-home/i,
  /home-design/i,
  /build-cost|cost-report|build-timeline/i,
  /\bbuilders?\b/i,
  /display-home/i,
  /renovat/i,         // renovation / renovations / renovate / renovator
  /extension/i,
  /\breno\b/i,
];

export function isBuildOrRenoContent(pathname) {
  if (!pathname) return false;
  return BUILD_RENO_PATH_PATTERNS.some((re) => re.test(pathname));
}

// ─── Page-pattern → recommendation table ────────────────────────
// Highest-specificity matches win. The resolver walks this top-to-
// bottom and returns the FIRST match. We aim to surface the most
// semantically relevant intro per page, every page type gets a
// distinct recommendation so the site feels broad and contextual,
// not "the Equable/House Inspect site".
//
// type values:
//   'land_builder_bundle' → Conveyancer + Builder bundle
//   'equable'             → partner:equable
//   'house_inspect'       → partner:house_inspect
//   'conveyancer'         → professional:conveyancer
//   'builder'             → professional:builder
//   'surveyor'            → professional:surveyor
//   'tradie:<name>'       → tradie:<name>
//   'stage_aware'         → use logged-in user's stage rec (skip otherwise)
const PAGE_PATTERN_ROUTES = [
  // ── COUNCIL / PLANNING / APPROVALS ── town planner (highest priority).
  // Town planners handle DAs, zoning, permits and council objections.
  // These rules sit at the very top because BOTH /building-in-X-council
  // and /knockdown-rebuild/council-approvals MUST win over the more
  // general /building-in-/ (suburb) and /knockdown-rebuild/ (builder)
  // patterns lower in the table.
  { match: /^\/building-in-[a-z-]+-council/i,              type: 'town_planner' },
  { match: /\/council-guide/i,                             type: 'town_planner' },
  { match: /\/council-approvals?/i,                        type: 'town_planner' },
  { match: /\/planning-approval/i,                         type: 'town_planner' },
  { match: /\/da-approval/i,                               type: 'town_planner' },
  { match: /\/development-application/i,                   type: 'town_planner' },
  { match: /\/zoning/i,                                    type: 'town_planner' },
  { match: /\/heritage-overlay/i,                          type: 'town_planner' },
  { match: /\/setbacks?/i,                                 type: 'town_planner' },

  // ── LAND / ESTATE / SUBURB / H&L ── conveyancer + builder bundle
  { match: /^\/land-estate/i,                              type: 'land_builder_bundle' },
  { match: /^\/estates?(\/|$)/i,                           type: 'land_builder_bundle' },
  { match: /^\/house-and-land/i,                           type: 'land_builder_bundle' },
  { match: /^\/building-in-/i,                             type: 'land_builder_bundle' },
  { match: /^\/suburbs?(\/|$)/i,                           type: 'land_builder_bundle' },
  { match: /\/choosing-land/i,                             type: 'land_builder_bundle' },

  // ── DESIGNS / FLOOR PLANS / DISPLAY HOMES ── find a builder
  { match: /^\/designs?(\/|$)/i,                           type: 'builder' },
  { match: /\/floor-plans?/i,                              type: 'builder' },
  { match: /\/display-homes?/i,                            type: 'builder' },
  { match: /\/home-builders?-/i,                           type: 'builder' },
  { match: /^\/builders?(\/|$)/i,                          type: 'builder' },
  { match: /^\/builder\//i,                                type: 'builder' },

  // ── BUILDING TYPES ── find a builder (specialist for that type)
  { match: /\/single-storey/i,                             type: 'builder' },
  { match: /\/double-storey/i,                             type: 'builder' },
  { match: /\/townhouse/i,                                 type: 'builder' },
  { match: /\/granny-flat/i,                               type: 'builder' },
  { match: /^\/journeys?\b/i,                              type: 'builder' },
  { match: /\/building-types?/i,                           type: 'builder' },
  { match: /\/custom-home/i,                               type: 'builder' },
  { match: /\/luxury-home/i,                               type: 'builder' },

  // ── KNOCKDOWN REBUILD ── needs a KDR specialist builder
  { match: /\/knockdown-rebuild/i,                         type: 'builder' },
  { match: /\/kdr\b/i,                                     type: 'builder' },

  // ── COUNCIL / PLANNING / SITE SURVEY ── (council/planning rules
  // already routed at the top of the table, these handle the
  // surveyor-specific subset that didn't match those.)
  // Site survey / set-out / contour pages, distinct from planning.
  { match: /\/site-survey/i,                               type: 'surveyor' },
  { match: /\/contour-survey/i,                            type: 'surveyor' },
  { match: /\/site-set-out/i,                              type: 'surveyor' },

  // ── INSPECTIONS / DEFECTS / HANDOVER ── House Inspect
  { match: /\/inspection/i,                                type: 'house_inspect' },
  { match: /\/defects?(\/|$|-)/i,                          type: 'house_inspect' },
  { match: /\/handover/i,                                  type: 'house_inspect' },
  { match: /\/pci\b/i,                                     type: 'house_inspect' },
  { match: /\/practical-completion/i,                      type: 'house_inspect' },
  { match: /\/pre-handover/i,                              type: 'house_inspect' },
  { match: /\/post-handover/i,                             type: 'house_inspect' },
  { match: /\/walkthrough/i,                               type: 'house_inspect' },

  // ── CONTRACT REVIEW / LEGAL ── conveyancer
  { match: /\/contract-review/i,                           type: 'conveyancer' },
  { match: /\/cooling-off/i,                               type: 'conveyancer' },
  { match: /\/easements?/i,                                type: 'conveyancer' },
  { match: /\/encumbrances?/i,                             type: 'conveyancer' },

  // ── FINANCE / COST / FHB ── Equable
  { match: /\/first-home-buyer/i,                          type: 'equable' },
  { match: /\/fhb/i,                                       type: 'equable' },
  { match: /\/finance/i,                                   type: 'equable' },
  { match: /\/loan/i,                                      type: 'equable' },
  { match: /\/mortgage/i,                                  type: 'equable' },
  { match: /\/borrowing/i,                                 type: 'equable' },
  { match: /\/deposit/i,                                   type: 'equable' },
  { match: /\/grants?\b/i,                                 type: 'equable' },
  { match: /\/fhog/i,                                      type: 'equable' },
  { match: /\/stamp-duty/i,                                type: 'equable' },
  { match: /\/build-cost/i,                                type: 'equable' },
  { match: /\/cost-report/i,                               type: 'equable' },
  { match: /\/build-vs-buy/i,                              type: 'equable' },
  { match: /\/budget/i,                                    type: 'equable' },

  // ── SPECIALIST BUILD-TYPE LANDING PAGES → Find-a-Builder wizard
  // The standalone build-type pages (e.g. /landscaping, /decking) and
  // their /building-types/X equivalents should route the user to the
  // 11-question wizard with the matching build_type already selected.
  // Sits ABOVE the broader /(reno|renovation)/ and /(deck|decking)/
  // patterns below so the exact landing-page URL wins over subpaths
  // like /renovations/costs (which stay on the renovation_builder
  // MiniMatcher route).
  { match: /^\/landscaping(\/|$)/i,                        type: 'wiz:Landscaping' },
  { match: /^\/swimming-pools?(\/|$)/i,                    type: 'wiz:Swimming Pools' },
  { match: /^\/verandahs?-pergolas?(\/|$)/i,               type: 'wiz:Pergolas/Verandahs' },
  { match: /^\/decking(\/|$)/i,                            type: 'wiz:Decking' },
  { match: /^\/sheds?-garages?(\/|$)/i,                    type: 'wiz:Sheds/Garages' },
  { match: /^\/renovations?(\/|$)/i,                       type: 'wiz:Renovations' },
  { match: /^\/extensions?(\/|$)/i,                        type: 'wiz:Extensions' },

  // ── RENOVATIONS / EXTENSIONS (subpaths) ── specialist builder per type
  { match: /\/(reno|renovation)/i,                         type: 'renovation_builder' },
  { match: /\/(extension|second-storey)/i,                 type: 'extension_builder' },
  { match: /\/(pergola|verandah|patio-roof)/i,             type: 'pergola_builder' },
  { match: /\/(deck|decking)/i,                            type: 'deck_builder' },
  { match: /\/(shed|garage-build)/i,                       type: 'shed_builder' },
  { match: /\/(window|door-install|sliding-door|bifold)/i, type: 'windows_doors' },
  { match: /\/(kitchen-reno|kitchen-design|kitchen-build)/i, type: 'kitchens' },

  // ── TRADE-SPECIFIC GUIDES ── matching tradie
  { match: /\/(tile|tiling|tiler)/i,                       type: 'tradie:tiler' },
  { match: /\/(paint|painting|painter)/i,                  type: 'tradie:painter' },
  { match: /\/(plumb|plumber|plumbing)/i,                  type: 'tradie:plumber' },
  { match: /\/(electric|electrician|sparky)/i,             type: 'tradie:electrician' },
  { match: /\/(landscaping|landscaper)/i,                  type: 'tradie:landscaper' },
  { match: /\/(pool-builder|swimming-pool)/i,              type: 'tradie:pool_builder' },
  { match: /\/(solar|battery)/i,                           type: 'tradie:solar' },
  { match: /\/(fenc|fencing)/i,                            type: 'tradie:fencer' },
  { match: /\/(stone|benchtop|caesarstone)/i,              type: 'tradie:stonemason' },
  { match: /\/(carpentry|carpenter|framing)/i,             type: 'tradie:carpenter' },
  { match: /\/(roof|roofing|roofer)/i,                     type: 'tradie:roofer' },
  { match: /\/(plaster|gyprock|cornice)/i,                 type: 'tradie:plasterer' },
  { match: /\/(concreter|slab|driveway)/i,                 type: 'tradie:concreter' },
  { match: /\/(waterproof|membrane)/i,                     type: 'tradie:waterproofer' },
  { match: /\/(glaz|glass|window-install)/i,               type: 'tradie:glazier' },
  { match: /\/(hvac|air-con|ducted)/i,                     type: 'tradie:hvac' },
  { match: /\/(flooring|carpet|laminate|vinyl)/i,          type: 'tradie:flooring' },
  { match: /\/(cabinetmaker|kitchen-install|joinery)/i,    type: 'tradie:cabinetmaker' },
  { match: /\/(handyman|odd-job)/i,                        type: 'tradie:handyman' },

  // ── CASE STUDIES / SUCCESS STORIES ── find a builder (see who BuildPilot already matched)
  { match: /\/case-studies?/i,                             type: 'builder' },
  { match: /\/real-build-stor/i,                           type: 'builder' },
  { match: /\/build-stor/i,                                type: 'builder' },

  // ── GUIDES INDEX / GLOSSARY ── stage-aware fallback (logged-in) or Equable (anon)
  { match: /^\/guides?(\/|$)/i,                            type: 'stage_aware' },
  { match: /\/terminology/i,                               type: 'stage_aware' },
  { match: /\/glossary/i,                                  type: 'stage_aware' },
  { match: /\/jargon/i,                                    type: 'stage_aware' },

  // ── BLOG / ARTICLES ── stage-aware fallback
  { match: /^\/blog(\/|$)/i,                               type: 'stage_aware' },
  { match: /^\/articles?(\/|$)/i,                          type: 'stage_aware' },
  { match: /^\/news(\/|$)/i,                               type: 'stage_aware' },

  // ── BUILDER TROUBLE / DISPUTES / CHANGING BUILDERS ── conveyancer
  // (legal-leaning issues, getting a contract reviewer + new builder
  // shortlist is the right play. We surface conveyancer first since
  // these readers usually need the contract help most urgently.)
  { match: /\/builder-went-bankrupt/i,                     type: 'conveyancer' },
  { match: /\/dispute-with-builder/i,                      type: 'conveyancer' },
  { match: /\/change-builders?\b/i,                        type: 'conveyancer' },
  { match: /\/changing-builders?\b/i,                      type: 'conveyancer' },
  { match: /\/common-builder-problems/i,                   type: 'conveyancer' },

  // ── ENERGY EFFICIENCY / NatHERS / BASIX ── energy assessor
  { match: /\/energy-efficient-home/i,                     type: 'energy_assessor' },
  { match: /\/nathers/i,                                   type: 'energy_assessor' },
  { match: /\/basix/i,                                     type: 'energy_assessor' },

  // ── SLOPING BLOCK / SITE CONDITIONS ── surveyor (assess block)
  { match: /\/sloping-block/i,                             type: 'surveyor' },
  { match: /\/block-assessment/i,                          type: 'surveyor' },
  { match: /\/soil-types?/i,                               type: 'surveyor' },

  // ── DESIGN GUIDES (vs the /designs/ catalogue) ── building designer
  { match: /\/home-design(\/|$)/i,                         type: 'building_designer' },
  { match: /\/floor-plan-design/i,                         type: 'building_designer' },
  { match: /^\/interior-design(\/|$)/i,                    type: 'interior_designer' },

  // ── OUTDOOR / LANDSCAPING / SWIMMING POOLS / DECK / SHED / PERGOLA ──
  // (Standalone landing-page URLs already handled at the top of this
  // table via `wiz:` types, they route to the Find-a-Builder wizard
  // with the build_type pre-selected.)


  // ── LEAD MAGNETS ── route to closest matching workflow
  { match: /\/lead-magnets?\/build-cost-benchmark/i,       type: 'equable' },
  { match: /\/lead-magnets?\/settlement-timeline/i,        type: 'equable' },
  { match: /\/lead-magnets?\/builder-shortlist/i,          type: 'builder' },

  // ── COPILOT GUIDES ── route each guide to the matcher for THIS topic.
  // Trades guide → tradie matcher (refined by ?stage= deep-link).
  // Pros guide   → pro matcher  (refined by ?decision= deep-link).
  { match: /^\/hiring-trades-guide/i,                       type: 'guide_tradie' },
  { match: /^\/hiring-professionals-guide/i,                type: 'guide_pro' },

  // ── COMMUNITY / 1:1 SUPPORT / HUB PAGES ── builder catch-all (handled below)
  // /community and /one-on-one-support already fall through naturally
  // to the builder catch-all when no other keyword matches.
];

/* Returns { variant, props } | null
 *
 *   variant: 'strip' | 'bundle'
 *   props: see PAGE_PATTERN_ROUTES for content per type
 *
 *   mode:
 *     'content' (default), ALWAYS page-content driven, regardless of
 *        login state. Used by inline placements (footer strip, in-page
 *        strips). Per user direction: inline content placements stay
 *        contextual to the page so the site feels topic-relevant even
 *        for logged-in users.
 *     'overlay', stage-driven when the user is logged in (FAB / banner
 *        / scroll-toast / exit-intent). Anonymous users still fall
 *        through to page-content routing.
 */
export function resolveSmartRecommendation({
  pathname,
  search = '',         // current URL query string, used to refine the rec
                       // when CoPilot guides pass ?stage= or ?decision=
  surface,          // 'inline' | 'footer' | 'fab' | 'banner' | 'toast' | 'exit-intent' | 'rail' | 'mid-article'
  mode = 'content', // 'content' | 'overlay'
  profile,          // optional user profile (state, building_stage, build_type)
  isLoggedIn,
  userState,        // override state from URL if specified
  financeSuppressed,// set when the user clicked "Already have finance sorted"
}) {
  if (isPathExcluded(pathname)) return null;

  // Normalise /building-types/X → /X so a single set of keyword
  // patterns covers both the standalone build-type pages
  // (e.g. /landscaping) and the canonical /building-types/landscaping
  // URLs reached via the Building Types menu.
  const normalisedPath = (pathname || '').replace(/^\/building-types\//i, '/');

  // Resolve the state from explicit signals only. NO silent SA default -
  // a logged-out NSW user must not see SA-only matches by accident. When
  // we genuinely don't know, leave it null so MiniMatcher prompts the
  // user to pick their state on the account step.
  const state = (profile?.state) || userState || null;
  const buildType = profile?.build_type || null;
  const buildStage = profile?.building_stage || null;
  const surfaceSuffix = (surface && typeof surface === 'string') ? surface : 'inline';
  const sourcePage = pathname;
  const placementId = (label) =>
    `smart-${surfaceSuffix}-${label}-${(pathname || '/').replace(/[^a-z0-9]+/gi, '-').replace(/^-+|-+$/g, '').slice(0, 40)}`;

  // Walk the routing table, first match wins.
  const route = PAGE_PATTERN_ROUTES.find((r) => r.match.test(normalisedPath));
  const routeType = route?.type;

  // ── OVERLAY + LOGGED-IN → STAGE WINS ──────────────────────────────
  // Per user direction (June 2026): overlay surfaces (FAB / banner /
  // toast / exit-intent) for a logged-in user ALWAYS reflect their
  // current build stage. Inline / content surfaces stay page-driven.
  //
  // This is a clean split, no "exception list" any more. The FAB
  // mirrors their stage; the footer strip mirrors the page. The two
  // intentionally diverge so the user sees both contextual help (page)
  // AND stage-appropriate next-step prompts (overlay).
  if (mode === 'overlay' && isLoggedIn && buildStage) {
    const stageRec = getStageRecommendation(buildStage);
    if (stageRec) {
      return {
        variant: 'strip',
        props: {
          category: stageRec.category,
          state,
          sourcePage,
          sourcePlacement: placementId('stage-' + buildStage.toLowerCase().replace(/\s+/g, '-')),
          eyebrow: stageRec.eyebrow,
          headline: stageRec.headline,
          body: stageRec.body,
          ctaLabel: stageRec.ctaLabel,
          // Builder stage recs → route to the full Find a Builder tool
          linkTo: stageRec.category === 'professional:builder' ? '/workspace/tools/find-a-builder' : null,
        },
      };
    }
  }

  // ── Build-Type Wizard route (wiz:Landscaping, wiz:Decking, etc.)
  // These specialist project types route straight to the Find-a-Builder
  // wizard with the build_type pre-selected, so the user doesn't have
  // to re-state the obvious. The wizard at /workspace/tools/find-a-builder
  // reads ?type= from the URL and seeds initial answers accordingly.
  if (routeType && routeType.startsWith('wiz:')) {
    const buildTypeLabel = routeType.slice(4);
    const friendly = buildTypeLabel.toLowerCase()
      .replace('pergolas/verandahs', 'pergola or verandah')
      .replace('sheds/garages', 'shed or garage')
      .replace(/s$/, '');
    const article = /^[aeiou]/.test(friendly) ? 'an' : 'a';
    return {
      variant: 'strip',
      props: {
        category: 'professional:builder',
        state,
        sourcePage,
        sourcePlacement: placementId('wiz-' + buildTypeLabel.toLowerCase().replace(/[^a-z]+/g, '-')),
        eyebrow: `${buildTypeLabel} specialist`,
        headline: `Match with ${article} ${friendly} builder for your project.`,
        body: `Tell us your block, budget and timeline. BuildPilot's wizard pre-selects ${buildTypeLabel} so you only answer the questions that matter to your project.`,
        ctaLabel: `Find ${article} ${friendly} builder`,
        linkTo: `/workspace/tools/find-a-builder?type=${encodeURIComponent(buildTypeLabel)}`,
      },
    };
  }

  // Tradie route → guard with the New-Home suppression rule.
  if (routeType && routeType.startsWith('tradie:')) {
    const newHome = /new\s*home|new-home|new_home/i.test(buildType || '');
    if (isLoggedIn && newHome) {
      // Fall through, Equable catch-all (suppress tradie nudge)
    } else {
      const tradieKey = routeType.split(':')[1];
      const friendly = tradieKey.replace(/_/g, ' ');
      return {
        variant: 'strip',
        props: {
          category: routeType,
          state,
          sourcePage,
          sourcePlacement: placementId(routeType.replace(':', '-')),
          eyebrow: friendly.charAt(0).toUpperCase() + friendly.slice(1),
          headline: `We can shortlist a ${friendly} in your area.`,
          body: `Tell us your suburb and the job, and we'll put forward listed ${friendly}s who cover your area.`,
          ctaLabel: `Find a ${friendly}`,
        },
      };
    }
  }

  if (routeType === 'land_builder_bundle') {
    return {
      variant: 'bundle',
      props: {
        eyebrow: 'Before you sign',
        headline: 'Two essentials before locking in land or a builder.',
        body: "A conveyancer reviews your land contract so you don't miss easements, encumbrances or developer conditions. A builder match shortlists the right team for your block, budget and timeline.",
        state,
        sourcePage,
        strips: [
          {
            category: 'professional:conveyancer',
            sourcePlacement: placementId('conveyancer'),
            eyebrow: 'Land contract review',
            headline: 'Get your land contract checked before you sign.',
            body: 'A conveyancer reviews cooling-off, encumbrances, developer conditions and easements, typical turnaround 3-5 business days.',
            ctaLabel: 'Find a conveyancer',
          },
          {
            category: 'professional:builder',
            sourcePlacement: placementId('builder'),
            eyebrow: 'Find a builder',
            headline: 'Match with builders who actually build on your block.',
            body: 'BuildPilot shortlists builders by block width, budget and timeline. You choose who to contact.',
            ctaLabel: 'Find a builder',
            // Builder match always goes to the full 11-question tool.
            linkTo: '/workspace/tools/find-a-builder',
          },
        ],
      },
    };
  }

  if (routeType === 'builder') {
    return {
      variant: 'strip',
      props: {
        category: 'professional:builder',
        state,
        sourcePage,
        sourcePlacement: placementId('builder'),
        eyebrow: 'Find a builder',
        headline: 'Match with builders who actually build on your block.',
        body: 'BuildPilot shortlists builders by block width, budget and timeline. You choose who to contact.',
        ctaLabel: 'Find a builder',
        // Always send to the full Find a Builder tool, never the small
        // 3-question popup. The wizard is the matchmaking engine.
        linkTo: '/workspace/tools/find-a-builder',
      },
    };
  }

  if (routeType === 'conveyancer') {
    return {
      variant: 'strip',
      props: {
        category: 'professional:conveyancer',
        state,
        sourcePage,
        sourcePlacement: placementId('conveyancer'),
        eyebrow: 'Land contract review',
        headline: 'Get your contract checked before you sign.',
        body: 'A conveyancer flags cooling-off windows, easements, encumbrances and developer conditions in 3-5 business days.',
        ctaLabel: 'Find a conveyancer',
      },
    };
  }

  if (routeType === 'surveyor') {
    return {
      variant: 'strip',
      props: {
        category: 'professional:surveyor',
        state,
        sourcePage,
        sourcePlacement: placementId('surveyor'),
        eyebrow: 'Site survey + set-out',
        headline: 'Get a contour, identification or site set-out survey.',
        body: "Surveyors handle site set-out and contour. Council approvals start a step earlier with a town planner. We'll introduce a surveyor who covers your area.",
        ctaLabel: 'Find a surveyor',
      },
    };
  }

  // ── COPILOT GUIDE: HIRING TRADES ───────────────────────────────
  // Page-wide default is a generic "shortlist a tradie" matcher. If a
  // `?stage=X` deep-link is present we refine to the trade most
  // relevant for that stage so the FAB label matches what the user
  // is currently reading.
  if (routeType === 'guide_tradie') {
    const params = new URLSearchParams(search || '');
    const stage = (params.get('stage') || '').toLowerCase();
    const STAGE_TO_TRADIE = {
      'site-prep':  { category: 'tradie:concreter',  ctaLabel: 'Find a concreter',  eyebrow: 'Site prep stage' },
      'frame-roof': { category: 'tradie:carpenter',  ctaLabel: 'Find a carpenter',  eyebrow: 'Frame and roof stage' },
      'lock-up':    { category: 'tradie:electrician', ctaLabel: 'Find an electrician', eyebrow: 'Lock-up stage' },
      'fit-off':    { category: 'tradie:painter',    ctaLabel: 'Find a painter',    eyebrow: 'Fit-off stage' },
      'defect':     { category: 'professional:house_inspect', ctaLabel: 'Find an inspector', eyebrow: 'Looking into a defect' },
    };
    const seed = STAGE_TO_TRADIE[stage] || {
      category: 'tradie:any',
      ctaLabel: 'Shortlist a tradie',
      eyebrow: 'Hiring trades',
    };
    return {
      variant: 'strip',
      props: {
        category: seed.category,
        state,
        sourcePage,
        sourcePlacement: placementId(`guide-tradie-${stage || 'default'}`),
        eyebrow: seed.eyebrow,
        headline: 'We can shortlist a trade for your build.',
        body: "Answer a few questions about your stage and suburb, and we'll put forward a short list of listed tradies who suit. You decide who to contact.",
        ctaLabel: seed.ctaLabel,
      },
    };
  }

  // ── COPILOT GUIDE: HIRING PROFESSIONALS ────────────────────────
  // Same idea, refined by `?decision=X` to surface the right pro.
  if (routeType === 'guide_pro') {
    const params = new URLSearchParams(search || '');
    const decision = (params.get('decision') || '').toLowerCase();
    const DECISION_TO_PRO = {
      'designing':  { category: 'professional:building_designer', ctaLabel: 'Find a designer',       eyebrow: 'Designing my home' },
      'approval':   { category: 'professional:town_planner',      ctaLabel: 'Find a town planner',   eyebrow: 'Council approval' },
      'contract':   { category: 'professional:conveyancer',       ctaLabel: 'Find a conveyancer',    eyebrow: 'Reviewing my contract' },
      'finance':    { category: 'professional:finance',           ctaLabel: 'Find a finance broker', eyebrow: 'Sorting finance' },
      'inspection': { category: 'professional:house_inspect',     ctaLabel: 'Find an inspector',     eyebrow: 'Build inspections' },
      'post-build': { category: 'professional:house_inspect',     ctaLabel: 'Find an inspector',     eyebrow: 'After the build' },
    };
    const seed = DECISION_TO_PRO[decision] || {
      category: 'professional:any',
      ctaLabel: 'Shortlist a professional',
      eyebrow: 'Hiring professionals',
    };
    return {
      variant: 'strip',
      props: {
        category: seed.category,
        state,
        sourcePage,
        sourcePlacement: placementId(`guide-pro-${decision || 'default'}`),
        eyebrow: seed.eyebrow,
        headline: 'We can shortlist a professional for your build.',
        body: "Answer a few questions about what you're working through, and we'll put forward a short list of listed professionals who fit. You decide who to contact.",
        ctaLabel: seed.ctaLabel,
      },
    };
  }

  if (routeType === 'town_planner') {
    return {
      variant: 'strip',
      props: {
        category: 'professional:town_planner',
        state,
        sourcePage,
        sourcePlacement: placementId('town-planner'),
        eyebrow: 'Council + planning',
        headline: 'Match with a town planner for your council.',
        body: 'Town planners handle DAs, zoning interpretation, heritage overlays and council objections. BuildPilot will introduce you to a PIA-accredited planner who works in your council area.',
        ctaLabel: 'Find a town planner',
      },
    };
  }

  if (routeType === 'energy_assessor') {
    return {
      variant: 'strip',
      props: {
        category: 'professional:energy_assessor',
        state,
        sourcePage,
        sourcePlacement: placementId('energy-assessor'),
        eyebrow: 'NatHERS + BASIX',
        headline: 'Lock in your energy rating before council submission.',
        body: 'An accredited energy assessor models your plans and flags glazing, insulation and orientation tweaks that lift your rating without lifting the build cost.',
        ctaLabel: 'Find an energy assessor',
      },
    };
  }

  if (routeType === 'building_designer') {
    return {
      variant: 'strip',
      props: {
        category: 'professional:building_designer',
        state,
        sourcePage,
        sourcePlacement: placementId('building-designer'),
        eyebrow: 'Independent designer',
        headline: 'Get an independent designer to pressure-test your plans.',
        body: "Builder's standard plans are designed to be built fast, not lived in well. An independent building designer spots orientation, flow, storage and light issues while you still have the chance to change them.",
        ctaLabel: 'Find a designer',
      },
    };
  }

  if (routeType === 'interior_designer') {
    return {
      variant: 'strip',
      props: {
        category: 'professional:interior_designer',
        state,
        sourcePage,
        sourcePlacement: placementId('interior-designer'),
        eyebrow: 'Interior design',
        headline: 'Match with an interior designer for your project.',
        body: 'Tapware, lighting, tiles, colours, finishes. An interior designer pulls every selection together into a coherent scheme. BuildPilot shortlists DIA-member designers in your area.',
        ctaLabel: 'Find an interior designer',
      },
    };
  }

  // ── Specialist builder branches (renovation / extension / pergola
  //    / deck / shed / windows-doors / kitchens), each gets a custom
  //    contextual headline so the strip reads as bespoke to the page.
  const SPECIALIST_COPY = {
    renovation_builder: {
      category: 'professional:renovation_builder',
      eyebrow: 'Renovation specialist',
      headline: 'Match with a renovation builder for your scope and budget.',
      body: "Renovation builders specialise in working with existing structures, they spot problems generic builders miss. BuildPilot shortlists by scope, budget and timeline.",
      ctaLabel: 'Find a renovation builder',
    },
    extension_builder: {
      category: 'professional:extension_builder',
      eyebrow: 'Extension specialist',
      headline: 'Match with an extension builder for your block.',
      body: "Adding to an existing home is a different craft to new builds, tie-ins, levels and matching finishes all need experience. BuildPilot shortlists extension specialists in your area.",
      ctaLabel: 'Find an extension builder',
    },
    pergola_builder: {
      category: 'professional:pergola_builder',
      eyebrow: 'Outdoor structures',
      headline: 'Match with a pergola or verandah builder.',
      body: 'BuildPilot shortlists pergola, verandah and patio specialists who handle design, council approvals (if required) and install.',
      ctaLabel: 'Find a pergola builder',
    },
    deck_builder: {
      category: 'professional:deck_builder',
      eyebrow: 'Decking specialist',
      headline: 'Match with a deck builder for your project.',
      body: 'Hardwood, composite or treated pine, every deck has tradeoffs. BuildPilot shortlists decking specialists who quote on your size, material and substructure.',
      ctaLabel: 'Find a deck builder',
    },
    shed_builder: {
      category: 'professional:shed_builder',
      eyebrow: 'Shed + garage specialist',
      headline: 'Match with a shed builder for your size and use.',
      body: 'Garage, workshop, storage or rural, shed builders quote on size, slab, cladding and council requirements. BuildPilot shortlists specialists in your area.',
      ctaLabel: 'Find a shed builder',
    },
    windows_doors: {
      category: 'professional:windows_doors',
      eyebrow: 'Windows + doors',
      headline: 'Match with a windows and doors specialist.',
      body: 'Aluminium, timber, uPVC or double-glazed, every option has thermal, security and cost tradeoffs. BuildPilot shortlists specialists for your project.',
      ctaLabel: 'Find a specialist',
    },
    kitchens: {
      category: 'professional:kitchens',
      eyebrow: 'Kitchen specialist',
      headline: 'Match with a kitchen designer + installer.',
      body: 'Full reno, cabinetry-only or new-build kitchen, kitchen specialists handle layout, cabinetry, benchtops, splashback and trades coordination.',
      ctaLabel: 'Find a kitchen specialist',
    },
  };
  if (routeType && SPECIALIST_COPY[routeType]) {
    const copy = SPECIALIST_COPY[routeType];
    return {
      variant: 'strip',
      props: {
        ...copy,
        state,
        sourcePage,
        sourcePlacement: placementId(routeType.replace(/_/g, '-')),
      },
    };
  }

  if (routeType === 'house_inspect') {
    return {
      variant: 'strip',
      props: {
        category: 'partner:house_inspect',
        state,
        sourcePage,
        sourcePlacement: placementId('house-inspect'),
        eyebrow: "BuildPilot's inspection partner",
        headline: 'Get an independent inspection before signing handover.',
        body: 'House Inspect Australia, our exclusive inspection partner, delivers photo-rich defect reports for frame, lock-up, PCI and post-handover stages.',
        ctaLabel: 'Book an inspection',
      },
    };
  }

  if (routeType === 'equable') {
    if (financeSuppressed) {
      // User has signalled they have finance sorted, fall through to the
      // next-best contextual rec instead of pushing finance again.
    } else {
      return {
        variant: 'strip',
        props: {
          category: 'partner:equable',
          state,
          sourcePage,
          sourcePlacement: placementId('finance-explore'),
          eyebrow: 'Construction finance',
          headline: 'Explore construction finance options for your build.',
          body: "Construction loans work very differently to standard home loans. BuildPilot will introduce you to a specialist who can map your borrowing power, FHB grant eligibility and progress-payment structure for the path you're considering.",
          ctaLabel: 'Explore finance options',
          allowFinanceSorted: true,
        },
      };
    }
  }

  // ── stage_aware fallback, use stage rec when logged in
  if (routeType === 'stage_aware' && isLoggedIn && buildStage) {
    const stageRec = getStageRecommendation(buildStage);
    if (stageRec) {
      return {
        variant: 'strip',
        props: {
          category: stageRec.category,
          state,
          sourcePage,
          sourcePlacement: placementId('stage-' + buildStage.toLowerCase().replace(/\s+/g, '-')),
          eyebrow: stageRec.eyebrow,
          headline: stageRec.headline,
          body: stageRec.body,
          ctaLabel: stageRec.ctaLabel,
        },
      };
    }
  }

  // ── Anonymous + everything else catch-all.
  // For pages that didn't match any specific keyword route above, we
  // ALTERNATE between three useful next-actions instead of always
  // promoting "Find a builder". The alternation is keyed off a stable
  // hash of the pathname so the same page always lands on the same
  // CTA (no random rotation between renders, no SEO duplicate-content
  // risk), but different pages across the site land on different CTAs
  // so the Footer rail feels varied rather than samey.
  //
  // Rotation set (3 buckets):
  //   0 → Find a builder (default for build-curious traffic)
  //   1 → Adelaide Build Cost Report (cost-curious traffic)
  //   2 → Talk to BuildPilot one on one (decision-stuck traffic)
  const hashSeed = (pathname || '/').split('').reduce(
    (acc, ch) => ((acc << 5) - acc + ch.charCodeAt(0)) | 0,
    0,
  );
  let bucket = Math.abs(hashSeed) % 3;
  // The One-on-One Support concierge (bucket 2) is only appropriate on
  // new-home-build or renovation content. On any other page, fall back to
  // the builder/cost next-action instead (keyed off the same hash so it
  // stays stable per page).
  if (bucket === 2 && !isBuildOrRenoContent(pathname)) {
    bucket = Math.abs(hashSeed) % 2;
  }

  if (bucket === 1) {
    return {
      variant: 'strip',
      props: {
        category: 'guide:cost-report',
        state,
        sourcePage,
        sourcePlacement: placementId('cost-report-catchall'),
        eyebrow: 'Build cost report',
        headline: 'See what an Adelaide build actually costs in 2026.',
        body: "Independent per square metre rates, site cost bands and the line items most quotes leave out. Updated with current builder, contract and project data from the Adelaide market.",
        ctaLabel: 'Read the cost report',
        linkTo: '/adelaide-build-cost-report-2026',
      },
    };
  }

  if (bucket === 2) {
    return {
      variant: 'strip',
      props: {
        category: 'service:one-on-one',
        state,
        sourcePage,
        sourcePlacement: placementId('one-on-one-catchall'),
        eyebrow: 'One on One Support',
        headline: 'Talk your build decision through with a real person.',
        body: state === 'SA'
          ? "Our Adelaide-based team will go through whatever you're weighing up, whether that's a quote comparison, a contract clause or a site issue, and help you work out a sensible next step."
          : "Our Australian-based team will go through whatever you're weighing up, whether that's a quote comparison, a contract clause or a site issue, and help you work out a sensible next step.",
        ctaLabel: 'See One on One Support',
        linkTo: '/one-on-one-support',
      },
    };
  }

  // bucket === 0 → Find a builder (existing copy)
  return {
    variant: 'strip',
    props: {
      category: 'professional:builder',
      state,
      sourcePage,
      sourcePlacement: placementId('builder-catchall'),
      eyebrow: 'Find a builder',
      headline: "Find a builder you can actually trust.",
      body: "Most Aussies sign with the first builder who returns their call. BuildPilot's 11-question matcher shortlists builders against your block, budget, design brief and timeline, so you compare like-for-like before you commit.",
      ctaLabel: 'Find a builder',
      linkTo: '/workspace/tools/find-a-builder',
    },
  };
}
